/**
 * Lead inbox types and labels.
 *
 * Hand-written rather than generated: the browser Supabase client is created
 * without a `<Database>` generic (see `src/lib/supabase.ts`), so `crm_lead_inbox`
 * needs a row shape here and `src/types/database.ts` needs no regeneration.
 * Every field below mirrors the inbox schema after its forward migrations;
 * those migrations are the source of truth.
 */

export const LEAD_INBOX_REVIEW_STATES = [
  'pending',
  'approved',
  'rejected',
  'snoozed',
  'suppressed',
] as const;

export type LeadInboxReviewState = (typeof LEAD_INBOX_REVIEW_STATES)[number];

export const LEAD_INBOX_SOURCES = ['trigify', 'csv_import', 'manual', 'savetnik'] as const;

export type LeadInboxSource = (typeof LEAD_INBOX_SOURCES)[number];

export type LeadInboxIdentityKind = 'linkedin' | 'email';

/**
 * The states an operator can still act on. `approved` and `rejected` are final;
 * `suppressed` never reaches the client at all (the RLS SELECT policy filters it).
 */
export const REVIEWABLE_STATES: readonly LeadInboxReviewState[] = ['pending', 'snoozed'];

/** One "Why selected" chip, captured at harvest/import time and never recomputed. */
export interface SelectionReason {
  kind: string;
  label: string;
}

export interface LeadInboxRow {
  id: string;
  organization_id: string;

  dedupe_key: string;
  identity_kind: LeadInboxIdentityKind;
  email: string | null;
  linkedin_url: string | null;
  first_name: string | null;
  last_name: string | null;
  job_title: string | null;
  company_name: string | null;
  location: string | null;
  language: string | null;

  source: LeadInboxSource;
  source_ref: string | null;
  source_label: string | null;
  campaign_key: string | null;

  lead_score: number | null;
  fit_score: number | null;
  intent_score: number | null;
  engagement_score: number | null;
  signal_type: string | null;
  signal_severity: string | null;
  pain_category: string | null;
  disc_type: string | null;
  post_content: string | null;
  post_author: string | null;
  signal_detected_at: string | null;

  selection_reasons: unknown;

  review_state: LeadInboxReviewState;
  review_note: string | null;
  snoozed_until: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;

  promoted_contact_id: string | null;
  promoted_at: string | null;

  harvest_run_id: string | null;
  created_at: string;
  updated_at: string;
}

export const REVIEW_STATE_LABELS: Record<LeadInboxReviewState, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  snoozed: 'Snoozed',
  suppressed: 'Suppressed',
};

export const REVIEW_STATE_COLORS: Record<LeadInboxReviewState, string> = {
  pending: 'bg-status-info/10 text-status-info border-status-info/30',
  approved: 'bg-status-success/10 text-status-success border-status-success/30',
  rejected: 'bg-surface text-muted-foreground border-border',
  snoozed: 'bg-status-warning/10 text-status-warning border-status-warning/30',
  suppressed: 'bg-surface text-text-tertiary border-border',
};

export const LEAD_INBOX_SOURCE_LABELS: Record<LeadInboxSource, string> = {
  trigify: 'Trigify',
  csv_import: 'CSV import',
  manual: 'Manual',
  savetnik: 'Savetnik chat',
};

/**
 * `selection_reasons` is a jsonb column, so what comes back is unknown at the
 * type level and untrusted at the value level. Anything that is not a
 * `{kind, label}` pair is dropped rather than rendered as `[object Object]`.
 */
export function parseSelectionReasons(value: unknown): SelectionReason[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((entry): SelectionReason[] => {
    if (typeof entry !== 'object' || entry === null) return [];
    const candidate = entry as { kind?: unknown; label?: unknown };
    if (typeof candidate.label !== 'string' || candidate.label.trim() === '') return [];
    return [
      {
        kind: typeof candidate.kind === 'string' ? candidate.kind : 'reason',
        label: candidate.label,
      },
    ];
  });
}

/** Display name for a queue row; never empty, so a row is always identifiable. */
export function leadDisplayName(
  row: Pick<LeadInboxRow, 'first_name' | 'last_name' | 'email' | 'linkedin_url'>,
): string {
  const name = [row.first_name, row.last_name]
    .map((part) => part?.trim() ?? '')
    .filter((part) => part !== '')
    .join(' ');

  if (name !== '') return name;

  if (row.email) return row.email;

  if (!row.linkedin_url) return 'Unknown lead';
  return row.linkedin_url.replace(/^https:\/\/www\.linkedin\.com\/in\//, '') || row.linkedin_url;
}
